import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { WalletContext } from './WalletContext';
import './GameSelection.css';

function GameSelection({ gameName, onBack, gameType = 'normal', gameId }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { walletBalance, updateWalletBalance } = useContext(WalletContext);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [user, setUser] = useState(null);

  const openTime = location.state?.openTime || '09:00';
  const closeTime = location.state?.closeTime;

  // Convert clock time to market-day minutes where 0 = 2:00 AM reset point.
  // Keeps 12:00 AM–1:59 AM in the same market day as the preceding evening.
  const toMarketMinutes = (h, m) => ((h * 60 + m) - 120 + 1440) % 1440;

  const isMarketClosed = () => {
    if (!closeTime) return false;
    const now = new Date();
    const kolkataTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
    const currentMarketMinutes = toMarketMinutes(kolkataTime.getHours(), kolkataTime.getMinutes());
    const [closeHours, closeMinutesVal] = closeTime.split(':').map(Number);
    const closeMarketMinutes = toMarketMinutes(closeHours, closeMinutesVal);
    return currentMarketMinutes >= closeMarketMinutes - 5;
  };

  // Jodi & Full Sangam close 5 minutes before the open time
  const isOpenBidClosed = () => {
    const now = new Date();
    const kolkataTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
    const currentMarketMinutes = toMarketMinutes(kolkataTime.getHours(), kolkataTime.getMinutes());
    const [openHours, openMinutesVal] = openTime.split(':').map(Number);
    const openMarketMinutes = toMarketMinutes(openHours, openMinutesVal);
    return currentMarketMinutes >= openMarketMinutes - 5;
  };

  // Load user data from localStorage and fetch wallet balance
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        
        // Fetch wallet balance using context
        if (parsedUser?.id) {
          updateWalletBalance(parsedUser.id);
        }
      } catch (err) {
        console.error('Error parsing user data:', err);
      }
    }
  }, [updateWalletBalance]);


  // Auto-navigate back to home when market closes while user is on this screen
  useEffect(() => {
    if (!closeTime) return;

    const now = new Date();
    const kolkataTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
    const currentMarketMinutes = toMarketMinutes(kolkataTime.getHours(), kolkataTime.getMinutes());
    const currentSeconds = kolkataTime.getSeconds();

    const [closeHours, closeMinutesVal] = closeTime.split(':').map(Number);
    const closeMarketMinutes = toMarketMinutes(closeHours, closeMinutesVal) - 5;

    const minutesUntilClose = closeMarketMinutes - currentMarketMinutes;

    if (minutesUntilClose <= 0) {
      // Already past effective close — go home immediately
      navigate('/');
      return;
    }

    const msUntilClose = (minutesUntilClose * 60 - currentSeconds) * 1000;
    const timeout = setTimeout(() => navigate('/'), msUntilClose);
    return () => clearTimeout(timeout);
  }, [closeTime, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Define game options based on game type
  const normalGameOptions = [
    {
      id: 2,
      title: 'Single Digit',
      icon: (
        <svg width="60" height="60" viewBox="0 0 60 60" fill="white">
          <rect x="15" y="15" width="30" height="30" rx="2" stroke="white" strokeWidth="3" fill="none"/>
          <circle cx="30" cy="30" r="4" fill="white"/>
        </svg>
      ),
      color: '#5B8DEE'
    },
    {
      id: 3,
      title: 'Jodi',
      icon: (
        <svg width="60" height="60" viewBox="0 0 60 60" fill="white">
          <rect x="15" y="15" width="30" height="30" rx="4" stroke="white" strokeWidth="3" fill="none"/>
          <circle cx="23" cy="23" r="3" fill="white"/>
          <circle cx="37" cy="37" r="3" fill="white"/>
        </svg>
      ),
      color: '#6C63FF'
    },
    {
      id: 4,
      title: 'Single Panna',
      icon: (
        <svg width="60" height="60" viewBox="0 0 60 60" fill="white">
          <rect x="20" y="15" width="20" height="30" rx="2" fill="white" fillOpacity="0.3"/>
          <path d="M25 18L25 42M30 18L30 42M35 18L35 42" stroke="white" strokeWidth="2"/>
          <circle cx="30" cy="30" r="3" fill="white"/>
        </svg>
      ),
      color: '#2ECC71'
    },
    {
      id: 5,
      title: 'Double Panna',
      icon: (
        <svg width="60" height="60" viewBox="0 0 60 60" fill="white">
          <rect x="18" y="15" width="20" height="28" rx="2" fill="white" fillOpacity="0.2" stroke="white" strokeWidth="2"/>
          <circle cx="24" cy="25" r="2.5" fill="white"/>
          <circle cx="32" cy="35" r="2.5" fill="white"/>
          <circle cx="28" cy="28" r="1.5" fill="white"/>
        </svg>
      ),
      color: '#9B59B6'
    },
    {
      id: 6,
      title: 'Triple Panna',
      icon: (
        <svg width="60" height="60" viewBox="0 0 60 60" fill="white">
          <rect x="18" y="15" width="24" height="30" rx="2" fill="white" fillOpacity="0.2" stroke="white" strokeWidth="2"/>
          <circle cx="23" cy="23" r="2" fill="white"/>
          <circle cx="30" cy="30" r="2" fill="white"/>
          <circle cx="37" cy="37" r="2" fill="white"/>
          <circle cx="23" cy="37" r="1.5" fill="white"/>
          <circle cx="37" cy="23" r="1.5" fill="white"/>
        </svg>
      ),
      color: '#E74C3C'
    },
    {
      id: 7,
      title: 'Half Sangam',
      icon: (
        <svg width="60" height="60" viewBox="0 0 60 60" fill="white">
          <rect x="15" y="18" width="14" height="24" rx="2" stroke="white" strokeWidth="2" fill="none"/>
          <rect x="31" y="18" width="14" height="24" rx="2" fill="white" fillOpacity="0.3"/>
          <circle cx="22" cy="30" r="2" fill="white"/>
          <circle cx="38" cy="26" r="1.5" fill="white"/>
          <circle cx="38" cy="34" r="1.5" fill="white"/>
        </svg>
      ),
      color: '#FF9800'
    },
    {
      id: 8,
      title: 'Full Sangam',
      icon: (
        <svg width="60" height="60" viewBox="0 0 60 60" fill="white">
          <rect x="14" y="18" width="14" height="24" rx="2" fill="white" fillOpacity="0.3"/>
          <rect x="32" y="18" width="14" height="24" rx="2" fill="white" fillOpacity="0.3"/>
          <circle cx="21" cy="26" r="2" fill="white"/>
          <circle cx="21" cy="34" r="2" fill="white"/>
          <circle cx="39" cy="26" r="2" fill="white"/>
          <circle cx="39" cy="34" r="2" fill="white"/>
        </svg>
      ),
      color: '#8BC34A'
    }
  ];

  const starlineGameOptions = [
    {
      id: 2,
      title: 'Single Digit',
      icon: (
        <svg width="60" height="60" viewBox="0 0 60 60" fill="white">
          <rect x="15" y="15" width="30" height="30" rx="2" stroke="white" strokeWidth="3" fill="none"/>
          <circle cx="30" cy="30" r="4" fill="white"/>
        </svg>
      ),
      color: '#5B8DEE'
    },
    {
      id: 4,
      title: 'Single Panna',
      icon: (
        <svg width="60" height="60" viewBox="0 0 60 60" fill="white">
          <rect x="20" y="15" width="20" height="30" rx="2" fill="white" fillOpacity="0.3"/>
          <path d="M25 18L25 42M30 18L30 42M35 18L35 42" stroke="white" strokeWidth="2"/>
          <circle cx="30" cy="30" r="3" fill="white"/>
        </svg>
      ),
      color: '#2ECC71'
    },
    {
      id: 5,
      title: 'Double Panna',
      icon: (
        <svg width="60" height="60" viewBox="0 0 60 60" fill="white">
          <rect x="18" y="15" width="20" height="28" rx="2" fill="white" fillOpacity="0.2" stroke="white" strokeWidth="2"/>
          <circle cx="24" cy="25" r="2.5" fill="white"/>
          <circle cx="32" cy="35" r="2.5" fill="white"/>
          <circle cx="28" cy="28" r="1.5" fill="white"/>
        </svg>
      ),
      color: '#9B59B6'
    },
    {
      id: 6,
      title: 'Triple Panna',
      icon: (
        <svg width="60" height="60" viewBox="0 0 60 60" fill="white">
          <rect x="18" y="15" width="24" height="30" rx="2" fill="white" fillOpacity="0.2" stroke="white" strokeWidth="2"/>
          <circle cx="23" cy="23" r="2" fill="white"/>
          <circle cx="30" cy="30" r="2" fill="white"/>
          <circle cx="37" cy="37" r="2" fill="white"/>
          <circle cx="23" cy="37" r="1.5" fill="white"/>
          <circle cx="37" cy="23" r="1.5" fill="white"/>
        </svg>
      ),
      color: '#E74C3C'
    }
  ];

  // Select the appropriate game options based on game type
  const gameOptions = gameType === 'starline' ? starlineGameOptions : normalGameOptions;

  // Determine the correct route path based on game type
  const getGameRoutePath = (optionId) => {
    const routeMap = {
      2: gameType === 'starline' ? `/starline-single-digit-game/${gameId}` : `/single-digit-game/${gameId}`,
      3: gameType === 'starline' ? `/starline-jodi-game/${gameId}` : `/jodi-game/${gameId}`,
      4: gameType === 'starline' ? `/starline-single-panna-game/${gameId}` : `/single-panna-game/${gameId}`,
      5: gameType === 'starline' ? `/starline-double-panna-game/${gameId}` : `/double-panna-game/${gameId}`,
      6: gameType === 'starline' ? `/starline-triple-panna-game/${gameId}` : `/triple-panna-game/${gameId}`,
      7: gameType === 'starline' ? `/starline-half-sangam-game/${gameId}` : `/half-sangam-game/${gameId}`,
      8: gameType === 'starline' ? `/starline-full-sangam-game/${gameId}` : `/full-sangam-game/${gameId}`
    };
    return routeMap[optionId] || `/single-digit-game/${gameId}`;
  };

  // Check if a game is restricted to open time only
  const isRestrictedGame = (gameId) => {
    return [8, 3].includes(gameId);
  };

  const showTimedToast = (message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  // Handle game option click
  const handleGameOptionClick = (option) => {
    if (isMarketClosed()) {
      showTimedToast('Market Closed');
      if (navigator.vibrate) navigator.vibrate(200);
      setTimeout(() => navigate('/'), 1500);
      return;
    }

    if (isRestrictedGame(option.id) && isOpenBidClosed()) {
      showTimedToast('Market Closed');
      if (navigator.vibrate) navigator.vibrate(200);
      // setTimeout(() => navigate('/'), 1500);
      return;
    }

    navigate(getGameRoutePath(option.id), {
      state: {
        gameId: gameId,
        gameName: gameName,
        openTime: location.state?.openTime,
        closeTime: location.state?.closeTime,
        gameTypeId: option.id
      }
    });
  };

  return (
    <div className="game-selection">
      {/* Header with Back Button */}
      <header className="game-selection-header">
        <button className="back-btn" onClick={onBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div className="game-selection-title">{gameName || 'SELECT GAME'}</div>
        <button className="wallet-btn" onClick={() => navigate("/funds")}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <rect x="2" y="6" width="20" height="14" rx="2" stroke="white" strokeWidth="2"/>
            <path d="M2 10H22" stroke="white" strokeWidth="2"/>
            <circle cx="18" cy="15" r="1.5" fill="white"/>
          </svg>
          <span className="wallet-balance">₹ {walletBalance}</span>
        </button>
      </header>

      {/* Game Options Grid */}
      <div className="game-options-container">
        <div className="game-options-grid">
          {gameOptions.map((option) => (
            <button
              key={option.id}
              className="game-option-card"
              onClick={() => handleGameOptionClick(option)}
              style={{ cursor: 'pointer' }}
            >
              <div className="game-option-icon" style={{ backgroundColor: option.color }}>
                {option.icon}
                <div className="icon-underline" style={{ backgroundColor: option.color }}></div>
              </div>
              <div className="game-option-title">{option.title}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Toast Notification */}
      {showToast && (
        <div className="toast-notification">
          {toastMessage}
        </div>
      )}
    </div>
  );
}

export default GameSelection;
